import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  Account,
  AccountInput,
  User,
  Users,
  DeleteUserInput,
  FreeTrials,
  Swagger,
  Subscription,
} from "../types";
import { RootState } from "@reducers/rootReducer";
import { toHyphenCase } from "@utils/helperFunctions";

const camelizeHyphenCaseKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelizeHyphenCaseKeys(v));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        ...(key.includes("-")
          ? {
              [key.replace(/-([a-z])/g, (g) => g[1].toUpperCase())]:
                camelizeHyphenCaseKeys(obj[key]),
            }
          : { [key]: camelizeHyphenCaseKeys(obj[key]) }),
      }),
      {}
    );
  }
  return obj;
};

const deepClone = (obj: any) => JSON.parse(JSON.stringify(obj));

const paramsSerializer = (params: any) => {
  const clonedParams = params ? deepClone(params) : {};
  const searchParams = new URLSearchParams();

  if (clonedParams?.page) {
    for (const [key, value] of Object.entries(clonedParams.page)) {
      searchParams.set(`page[${key}]`, String(value));
    }
    delete clonedParams.page;
  }
  if (clonedParams?.include && Array.isArray(clonedParams.include)) {
    searchParams.set("include", clonedParams.include.join(","));
    delete clonedParams.include;
  }
  if (clonedParams?.sort && Array.isArray(clonedParams.sort)) {
    searchParams.set("sort", clonedParams.sort.join(","));
    delete clonedParams.sort;
  }

  if (clonedParams) {
    for (const [key, value] of Object.entries(clonedParams)) {
      if (value && typeof value === "object") {
        for (const [subKey, subValue] of Object.entries(value)) {
          searchParams.set(`${key}[${subKey}]`, subValue as string);
        }
      } else {
        searchParams.set(key, value as string);
      }
    }
  }

  return searchParams.toString();
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SCOPESTACK_API_HOST as string,
  prepareHeaders: (headers: Headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    headers.set("Content-Type", "application/vnd.api+json");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
  paramsSerializer,
});

export const scopeStackApi = createApi({
  reducerPath: "scopeStackApi",
  baseQuery,
  endpoints: (builder) => ({
    getCompanyAccount: builder.query<Account, string>({
      query: (slug = "") => `v1/accounts/${slug}`,
      transformResponse: (response: { data: Account }) => response.data,
    }),
    updateCompanyAccount: builder.mutation<Account, AccountInput>({
      query: ({ slug, ...payload }) => ({
        url: `v1/accounts/${slug}`,
        method: "PATCH",
        body: toHyphenCase(payload),
      }),
    }),
    getMe: builder.query<User, void>({
      query: () => "v1/me",
      transformResponse: (response: { data: User }) => response.data,
    }),
    getUsers: builder.query<
      Users,
      { slug: string; currentPage?: number; pageSize?: number }
    >({
      query: ({ slug, currentPage, pageSize }) =>
        `${slug}/v1/users?page[size]=${
          pageSize ? pageSize : 10
        }&page[number]=${currentPage}`,
    }),
    removeUser: builder.mutation<
      void,
      { slug: string; userInfo: DeleteUserInput }
    >({
      query: ({ slug, userInfo }) => ({
        url: `${slug}/v1/users/${userInfo.data.id}`,
        method: "DELETE",
        body: userInfo,
      }),
    }),
    patchUser: builder.mutation<User, { slug: string; user: User }>({
      query: ({ slug, user }) => ({
        url: `${slug}/v1/users/${user.id}`,
        method: "PATCH",
        body: toHyphenCase({ data: { ...user, id: user.id } }),
      }),
    }),
    getSwagger: builder.query<Swagger, null>({
      query: () => "docs/v1/swagger.json",
      transformResponse: (response: any): Swagger => {
        const accountProperties =
          response.components.schemas.v1_AccountResource.properties.attributes
            .properties;
        return {
          shortDateFormat: accountProperties["short-date-format"]
            .enum as string[],
          longDateFormat: accountProperties["long-date-format"]
            .enum as string[],
          timeFormat: accountProperties["time-format"].enum as string[],
          timeZone: accountProperties["time-zone"].enum as string[],
        };
      },
    }),
    getFreeTrials: builder.query<FreeTrials, string>({
      query: (slug = "") => `${slug}/v1/free-trials`,
    }),
  }),
});

// auto-generated hooks for every endpoint
export const {
  useGetCompanyAccountQuery,
  useUpdateCompanyAccountMutation,
  useGetMeQuery,
  useGetUsersQuery,
  useRemoveUserMutation,
  usePatchUserMutation,
  useGetSwaggerQuery,
  useGetFreeTrialsQuery,
} = scopeStackApi;
